import React, { useState } from 'react';
import { cn } from '../../utils/cn';
import Icon from '../AppIcon';

const Input = React.forwardRef(({
    className,
    type = "text",
    label,
    description,
    error,
    success,
    required = false,
    iconName,
    iconPosition = 'left',
    fullWidth = false,
    loading = false,
    clearable = false,
    onClear,
    ...props
}, ref) => {
    const [showPassword, setShowPassword] = useState(false);
    const [isFocused, setIsFocused] = useState(false);

    const handleClear = () => {
        if (onClear) {
            onClear();
        } else if (props.onChange) {
            props.onChange({ target: { value: '' } });
        }
    };

    const togglePasswordVisibility = () => {
        setShowPassword(!showPassword);
    };

    const inputType = type === 'password' && showPassword ? 'text' : type;

    return (
        <div className={cn("w-full", fullWidth && "w-full")}>
            {label && (
                <label className="block text-sm font-medium text-text-primary mb-2">
                    {label}
                    {required && <span className="text-error ml-1">*</span>}
                </label>
            )}
            
            <div className="relative">
                {/* Left Icon */}
                {iconName && iconPosition === 'left' && (
                    <div className="absolute left-3 top-1/2 transform -translate-y-1/2 pointer-events-none">
                        <Icon 
                            name={iconName} 
                            size={16} 
                            color={error ? 'var(--color-error)' : isFocused ? 'var(--color-primary)' : 'var(--color-text-secondary)'} 
                        />
                    </div>
                )}

                <input
                    type={inputType}
                    className={cn(
                        "flex h-10 w-full rounded-md border border-border bg-background input-spacing-md text-sm ring-offset-background transition-all duration-200",
                        "file:border-0 file:bg-transparent file:text-sm file:font-medium",
                        "placeholder:text-text-secondary",
                        "input-focus",
                        "disabled:cursor-not-allowed disabled:opacity-50",
                        iconName && iconPosition === 'left' && "!pl-11",
                        (iconName && iconPosition === 'right') || type === 'password' || clearable ? "pr-10" : "",
                        error && "border-error focus-visible:ring-error",
                        success && "border-success focus-visible:ring-success",
                        loading && "cursor-wait",
                        className
                    )}
                    ref={ref}
                    onFocus={() => setIsFocused(true)}
                    onBlur={() => setIsFocused(false)}
                    aria-invalid={error ? 'true' : 'false'}
                    aria-describedby={error ? `${props.id}-error` : description ? `${props.id}-description` : undefined}
                    {...props}
                />

                {/* Right Icons */}
                <div className="absolute right-3 top-1/2 transform -translate-y-1/2 flex items-center space-x-1">
                    {loading && (
                        <div className="animate-spin">
                            <Icon name="Loader2" size={16} color="var(--color-primary)" />
                        </div>
                    )}
                    
                    {clearable && props.value && !loading && (
                        <button
                            type="button"
                            onClick={handleClear}
                            className="p-0.5 hover:bg-muted rounded"
                            tabIndex={-1}
                        >
                            <Icon name="X" size={14} color="var(--color-text-secondary)" />
                        </button>
                    )}
                    
                    {type === 'password' && !loading && (
                        <button
                            type="button"
                            onClick={togglePasswordVisibility}
                            className="p-0.5 hover:bg-muted rounded"
                            tabIndex={-1}
                        >
                            <Icon 
                                name={showPassword ? 'EyeOff' : 'Eye'} 
                                size={16} 
                                color="var(--color-text-secondary)" 
                            />
                        </button>
                    )}
                    
                    {iconName && iconPosition === 'right' && !loading && type !== 'password' && !clearable && (
                        <Icon 
                            name={iconName} 
                            size={16} 
                            color={error ? 'var(--color-error)' : isFocused ? 'var(--color-primary)' : 'var(--color-text-secondary)'} 
                        />
                    )}
                </div>
            </div>

            {/* Description */}
            {description && !error && (
                <p id={`${props.id}-description`} className="mt-2 text-sm text-text-secondary">
                    {description}
                </p>
            )}

            {/* Error Message */}
            {error && (
                <p id={`${props.id}-error`} className="mt-2 text-sm text-error flex items-center">
                    <Icon name="AlertCircle" size={14} className="mr-1" color="var(--color-error)" />
                    {error}
                </p>
            )}

            {/* Success Message */}
            {success && (
                <p className="mt-2 text-sm text-success flex items-center">
                    <Icon name="CheckCircle" size={14} className="mr-1" color="var(--color-success)" />
                    {success}
                </p>
            )}
        </div>
    );
});

Input.displayName = "Input";

export default Input;